// External
import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, NavigationStart, NavigationCancel } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import { filter, takeUntil } from 'rxjs/operators';

// Internal
import { TooltipService } from '../app/common/services/global/tooltip/tooltip.service';
import { ConfigService } from './common/config/config.service';
import { fadeAnimation } from './../ux/animations/fade.animations';
import { ValuesService } from './common/values/values.service';
import { ScriptService } from './common/services/core/scripts.service';
import { Subject } from 'rxjs';
import { MessageService } from './common/services/core/message.service';
import { AuthService } from './common/services/global/auth/auth-service.service';
import { ThemeService } from './common/services/core/theme.service';
import { AdobeDataLayerService } from './common/services/core/adobe.datalayer.service';
import { UsefulService } from './common/services/global/useful/useful.service';
import { ProductsConfigService } from './common/config/products.config.service';
import { ProfilesService } from './common/services/process/profiles/profiles.service';
import { ModalRoutelessService } from './common/components/ui/ui-modal-routeless/modal.routeless.service';
import { NavigationService } from './common/navigation/navigation.service';
import { CommercialidsService } from './common/services/process/commercialids/commercialIds.service';
import { DevicesService } from './common/services/process/devices/devices.service';
import { OffersService } from './common/services/process/offers/offers.service';
import { PrivacyService } from './common/services/process/privacy/privacy.service';
import { PaymentsService } from './common/services/process/subscriptions/payments.service';
import { Scripts } from './common/services/core/models/Scripts.model';
import { Outlet, OutletStatus, OutletsStatusService } from './common/services/core/outlets.status.service';
import { ServiceWorkerModule } from '@angular/service-worker';

// * Extend the existing window interface to tell it about the new property "adobeDataLayer"
declare global {
    interface Window { adobeDataLayer: any; }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    animations: [fadeAnimation],
    host: {'[class.elevation-0]': 'true'}
})

export class AppComponent implements OnInit {

    triedToLoadAdobe = false;
    adobeAlreadySent = false;
    url = "";
    auxUrl;
    private readonly onDestroy$: Subject<void> = new Subject<void>();
    _storedPath;
    isPathAllowedForCookieBot() {
        const disallowedPaths = this.valuesService.dissallowedPathsForCookieBot;
        return !disallowedPaths.has(this.location.path());
    }

    constructor(
        private readonly router              : Router,
        private readonly authService        : AuthService,
        private readonly valuesService       : ValuesService,
        private readonly meta                : Meta,
        private  readonly configService      : ConfigService,
        private  readonly location           : Location,
        public  readonly tooltipService      : TooltipService,
        private  readonly translateService   : TranslateService,
        private readonly scriptService       : ScriptService,
        private readonly messageService      : MessageService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        public readonly usefulService        : UsefulService,
        private readonly themeService        : ThemeService,
        private readonly productsConfigService     : ProductsConfigService,
        private readonly profileService: ProfilesService,
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly navigationService: NavigationService,
        private readonly commercialIdsService: CommercialidsService,
        private readonly devicesService: DevicesService,
        private readonly offersService: OffersService,
        private readonly privacyService: PrivacyService,
        private readonly paymentsService: PaymentsService,
        private readonly outletsStatusService: OutletsStatusService,
        private readonly sw: ServiceWorkerModule
    ) {
        // * Create empty array for data layer
        // window.adobeDataLayer = window.adobeDataLayer || [];
        this.adobeDataLayerService.setPageCategory();
        this.adobeDataLayerService.setOs();
        this.adobeDataLayerService.setPageAttributes();
        this.adobeDataLayerService.setServerName();

        this.themeService.initTheme();
        if (this.isPathAllowedForCookieBot()) {
            this.scriptService.load(Scripts.CookieBot);
        }

        // ! CWEBII-3300 - Temporary until vpn page will be removed from Central ( Adobe should not load on VPN account page )
        const vpnPath = this.valuesService.centralPaths.services.path.concat(this.valuesService.centralPaths.services.vpn.path,
                                                                                this.valuesService.centralPaths.services.vpn.account.path);
        if (this.location.path().indexOf(vpnPath) === -1) {
            this.adobeDataLayerService.loadApi()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: () => true,
                error: () => true
            });
        }

        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            if (message.text === this.valuesService.events.targetActivityLoaded
                && this.url && this.url !== '/'
                && this.triedToLoadAdobe
                && !this.adobeAlreadySent
                && !this.url.includes(vpnPath)) {
                this.triedToLoadAdobe = false;
                this.adobeAlreadySent = true;
            }
        });

        this.setPWA();
    }

    decideOutlets(route) {
        if (route.url === '/' && !this.authService.checkLogin()) {
            this.router.navigate([this.valuesService.centralPaths['home'].path]);
            return;
        }
    }

    ngOnInit() {
        this.translateService.get('main.meta.description')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
            next: res => {
                this.meta.updateTag({
                    name: 'description',
                    content: res.replace('{{manufacturerName}}', this.productsConfigService.getManufacturerName()).replace('{{siteName}}', this.configService.config.siteName) },
                    'name=description'
                );
            },
            error: () =>{
                this.meta.updateTag({
                    name: 'description',
                    content: 'Emsisoft Central is your control panel for subscription management, product installation, device security monitoring, and 24/7 support.' },
                    'name=description'
                );
            }
        });

        if (!navigator.cookieEnabled) {
            this.router.navigate([this.valuesService.publicCentralPaths.cookies.path]);
            return;
        }

        //* Listener for location change end (Adobe)
        this.router.events.pipe(
            takeUntil(this.onDestroy$),
            filter(event => event instanceof NavigationStart || event instanceof NavigationEnd || event instanceof NavigationCancel)
        ).subscribe((event: NavigationStart|NavigationEnd|NavigationCancel) => {
            // sent adobe info only when the path from the navigation event is not empty or === to "/"
            // this + logic from constructor handles the following cases:
            // adobe loads, start navigation, end navgation
            // start navigation, adobe loads, end navigation
            // start navigation, ened navigation, adobe loads
            if (event instanceof NavigationStart) {
                this.outletsStatusService.setStatus(Outlet.NAVIGATION, OutletStatus.PENDING);
                this.navigationService.setIsNavigationInProgress(true);

                let _url = event.url;


                // ! Detect path change
                if (this._storedPath !== _url) {
                    //! For leaving dashboard
                    this.outletsStatusService.setStatus(Outlet.PAGE, OutletStatus.PENDING);
                    this.adobeDataLayerService.resetInteractionValues();
                    this.adobeDataLayerService.resetPathSections();
                    this.adobeDataLayerService.resetUserDeviceValues();
                    this.modalRoutelessService.setBufferItem(this.valuesService.modalBufferKeys.MSP_ONBOARDING, false);
                    this.modalRoutelessService.setBufferItem(this.valuesService.modalBufferKeys.MSP_INSTALL_MODAL, false); //! NOT NEEDED NOW
                }
                // ! ------------------

                this.adobeAlreadySent = false;
                this.url = event.url;


                this.adobeDataLayerService.setUserLoggedIn(this.authService.checkLogin());
            } else if (event instanceof NavigationEnd) {
                let _url = event.urlAfterRedirects;
                _url = this.profileService.sanitizeUrlForParental(_url);

                if (this._storedPath !== _url) {
                    if (!this.modalRoutelessService.getPendingModal()) {
                        this.adobeDataLayerService.setUrl(_url);
                        this.adobeDataLayerService.setDestinationUrl(_url);
                        this.adobeDataLayerService.triggerPageLoad();
                        if (this._storedPath !== undefined) {
                            this.adobeDataLayerService.resetTrackingID();
                        }
                    }
                    this.adobeDataLayerService.setUrl(_url);
                    this._storedPath = _url;
                }
                this.navigationService.setIsNavigationInProgress(false);
                this.messageService.sendMessage(this.valuesService.events.dataResolverDone, {});
                this.outletsStatusService.setStatus(Outlet.NAVIGATION, OutletStatus.DONE);
            } else if (event instanceof NavigationCancel) {
                this.commercialIdsService.resetInProgressFlags();
                this.devicesService.resetInProgressFlags();
                this.offersService.resetInProgressFlags();
                this.privacyService.resetInProgressFlags();
            }

            if (!this.scriptService.isAdobeScriptLoaded()) {
                this.triedToLoadAdobe = true;
            } else if (event instanceof NavigationStart && event?.url && event.url !== '/') {
                this.adobeAlreadySent = true;
                this.triedToLoadAdobe = false;
            } else if (event instanceof NavigationEnd && event?.urlAfterRedirects && event.urlAfterRedirects !== "/" && !this.adobeAlreadySent) {
                this.triedToLoadAdobe = false;
                this.adobeAlreadySent = true;
            }
        });

        this.router.events.pipe(
            takeUntil(this.onDestroy$),
            filter(event => event instanceof NavigationStart)
        ).subscribe((event: NavigationStart) => {
            this.decideOutlets(event);
        });
    }


    /**
     *
     * Set pwa status based on config
     * active = true -> set pwa active and init SW
     * active = false -> unregister old SW (if exist)
     * @private
     * @memberof AppComponent
     */
    private setPWA(): void {
        if (this.configService.getPwaActive()) {
            //Add manifest
            let element = document.createElement('link');
            element.setAttribute('rel', 'manifest');
            element.setAttribute('href', this.configService.getPwaManifestPath());
            document.querySelector('head').appendChild(element);

            //Register SW
            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.register(this.configService.getPwaSwPath()).then(
                  () => {
                    console.log("Service worker registration succeeded");
                  },
                  (error) => {
                    console.error(`Service worker registration failed: ${error}`);
                  },
                );
              } else {
                console.error("Service workers are not supported.");
              }
        } else {
            //Unregister SW if PWA is off
            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.getRegistration('/').then(
                  (registration) => {
                    if (registration) {
                        registration.unregister().then((boolean) => {
                            if (boolean) {
                                console.log("Service worker unregistration succeeded");
                            } else {
                                console.log("Service worker unregistration faild");
                            }
                          });
                    }
                  },
                  (error) => {
                    console.error(`Service worker unregistration failed: ${error}`);
                  },
                );
              }
        }
    }
}
